import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import useQueryParams from 'src/shared/hooks/useQueryParams';

const isTestEnvironment = import.meta.env.DEV || import.meta.env.VITE_ENABLE_TEST_PAGES === 'true';

// Test schemas
const enumSchema = z.enum(['value1', 'value2', 'value3']).default('value1');
const numberSchema = z.coerce.number().default(42);

export default function UseQueryParamsTestPage() {
  const navigate = useNavigate();

  // Redirect if not in test environment (similar to crash page)
  useEffect(() => {
    if (!isTestEnvironment) {
      navigate({ to: '/', replace: true });
    }
  }, [navigate]);

  // Test basic functionality
  const { param: basicParam, setParam: setBasicParam, deleteParam: deleteBasicParam } = useQueryParams('basicParam');

  // Test with validator (old syntax)
  const { param: enumParamOld } = useQueryParams('enumParamOld', undefined, enumSchema);

  // Test with validatorOptions (new syntax)
  const { param: enumParamNew, setParam: setEnumParamNew } = useQueryParams('enumParamNew', undefined, {
    validator: enumSchema,
    enforceDefault: true,
  });

  const { param: enumParamNoEnforce } = useQueryParams('enumParamNoEnforce', undefined, {
    validator: enumSchema,
    enforceDefault: false,
  });

  // Test with number schema
  const { param: numberParam } = useQueryParams('numberParam', undefined, {
    validator: numberSchema,
    enforceDefault: true,
  });

  // Test addParam and deleteSpecific
  const { param: multiParam, addParam: addMultiParam, deleteSpecific: deleteMultiParam } = useQueryParams('multiParam');

  // Test deleteParam
  const {
    param: deleteTestParam,
    setParam: setDeleteTestParam,
    deleteParam: deleteDeleteTestParam,
  } = useQueryParams('deleteTestParam');

  // Don't render in production
  if (!isTestEnvironment) {
    return null;
  }

  return (
    <div data-testid="useQueryParams-test-page" style={{ padding: '20px', fontFamily: 'monospace' }}>
      <h1>useQueryParams Test Page</h1>
      <p style={{ color: '#666', fontSize: '12px' }}>This page is only available in development/test environments</p>

      <div style={{ marginBottom: '20px', border: '1px solid #ddd', padding: '10px' }}>
        <h2>Basic Param</h2>
        <div data-testid="basic-param-value" style={{ marginBottom: '10px' }}>
          Value: {basicParam}
        </div>
        <div style={{ marginBottom: '10px' }}>
          Current URL: {typeof window !== 'undefined' ? window.location.search : ''}
        </div>
        <button
          data-testid="set-basic-param"
          onClick={() => setBasicParam('testValue')}
          style={{ marginRight: '10px', padding: '5px 10px' }}
        >
          Set Basic Param
        </button>
        <button data-testid="delete-basic-param" onClick={deleteBasicParam} style={{ padding: '5px 10px' }}>
          Delete Basic Param
        </button>
      </div>

      <div style={{ marginBottom: '20px', border: '1px solid #ddd', padding: '10px' }}>
        <h2>Enum Param (Old Syntax)</h2>
        <div data-testid="enum-param-old-value">Value: {String(enumParamOld)}</div>
      </div>

      <div style={{ marginBottom: '20px', border: '1px solid #ddd', padding: '10px' }}>
        <h2>Enum Param (New Syntax - enforceDefault: true)</h2>
        <div data-testid="enum-param-new-value" style={{ marginBottom: '10px' }}>
          Value: {String(enumParamNew)}
        </div>
        <button
          data-testid="set-enum-param-new"
          onClick={() => setEnumParamNew('value2')}
          style={{ padding: '5px 10px' }}
        >
          Set to value2
        </button>
      </div>

      <div style={{ marginBottom: '20px', border: '1px solid #ddd', padding: '10px' }}>
        <h2>Enum Param (New Syntax - enforceDefault: false)</h2>
        <div data-testid="enum-param-no-enforce-value">Value: {String(enumParamNoEnforce)}</div>
      </div>

      <div style={{ marginBottom: '20px', border: '1px solid #ddd', padding: '10px' }}>
        <h2>Number Param</h2>
        <div data-testid="number-param-value">Value: {String(numberParam)}</div>
      </div>

      <div style={{ marginBottom: '20px', border: '1px solid #ddd', padding: '10px' }}>
        <h2>Multi Param (addParam/deleteSpecific)</h2>
        <div data-testid="multi-param-value" style={{ marginBottom: '10px' }}>
          Value: {multiParam}
        </div>
        <button
          data-testid="add-multi-param"
          onClick={() => addMultiParam('value1')}
          style={{ marginRight: '10px', padding: '5px 10px' }}
        >
          Add value1
        </button>
        <button
          data-testid="delete-multi-param"
          onClick={() => deleteMultiParam('value1')}
          style={{ padding: '5px 10px' }}
        >
          Delete value1
        </button>
      </div>

      <div style={{ marginBottom: '20px', border: '1px solid #ddd', padding: '10px' }}>
        <h2>Delete Param Test</h2>
        <div data-testid="delete-test-param-value" style={{ marginBottom: '10px' }}>
          Value: {deleteTestParam}
        </div>
        <button
          data-testid="set-delete-test-param"
          onClick={() => setDeleteTestParam('test')}
          style={{ marginRight: '10px', padding: '5px 10px' }}
        >
          Set deleteTestParam
        </button>
        <button data-testid="delete-delete-test-param" onClick={deleteDeleteTestParam} style={{ padding: '5px 10px' }}>
          Delete deleteTestParam
        </button>
      </div>
    </div>
  );
}
